import {
  areAllPricesSoldOut,
  isResolvedProductSoldOut,
  isResolvedVarietySoldOut,
} from './productAvailability';

export const POS_SOLD_OUT_MODE_NORMAL = 'normal';
export const POS_SOLD_OUT_MODE_BOTTOM = 'bottom';
export const POS_SOLD_OUT_MODE_HIDDEN = 'hidden';

export const POS_SOLD_OUT_DISPLAY_OPTIONS = [
  {
    value: POS_SOLD_OUT_MODE_NORMAL,
    label: 'Mostrar normalmente',
    description: 'Mantiene los productos agotados en su posicion actual.',
  },
  {
    value: POS_SOLD_OUT_MODE_BOTTOM,
    label: 'Mostrar al final',
    description: 'Mueve los productos agotados al final de cada grupo.',
  },
  {
    value: POS_SOLD_OUT_MODE_HIDDEN,
    label: 'Ocultar',
    description: 'No muestra productos agotados en la busqueda manual.',
  },
];

const POS_SOLD_OUT_MODES = new Set(
  POS_SOLD_OUT_DISPLAY_OPTIONS.map((option) => option.value)
);

export const normalizePosSoldOutMode = (value) => {
  if (!value) return POS_SOLD_OUT_MODE_NORMAL;
  const normalized = String(value).trim().toLowerCase();
  return POS_SOLD_OUT_MODES.has(normalized) ? normalized : POS_SOLD_OUT_MODE_NORMAL;
};

export const isPosVarietySoldOut = (variety) => {
  return isResolvedVarietySoldOut(variety);
};

export const isPosProductSoldOut = (product) => {
  return isResolvedProductSoldOut(product);
};

export const getPosVarietyDisplayPrices = (variety, priceFilter) => (
  (variety?.VarietyPrices || []).filter((price) => {
    if (!price || price.price === null || price.price === undefined) return false;
    return typeof priceFilter === 'function' ? priceFilter(price) : true;
  })
);

export const isPosVarietySoldOutForPrices = (variety, priceFilter) => {
  const prices = getPosVarietyDisplayPrices(variety, priceFilter);
  return areAllPricesSoldOut(prices);
};

export const isPosProductSoldOutForPrices = (product, priceFilter) => {
  const varieties = (product?.Varieties || []).filter(
    (variety) => getPosVarietyDisplayPrices(variety, priceFilter).length > 0
  );
  if (!varieties.length) return false;
  return varieties.every((variety) => isPosVarietySoldOutForPrices(variety, priceFilter));
};

export const filterProductsBySoldOutDisplay = (
  products,
  soldOutMode,
  isProductSoldOut = isPosProductSoldOut
) => {
  const mode = normalizePosSoldOutMode(soldOutMode);
  const list = Array.isArray(products) ? products : [];
  if (mode !== POS_SOLD_OUT_MODE_HIDDEN) return list;
  return list.filter((product) => !isProductSoldOut(product));
};

export const filterVarietiesBySoldOutDisplay = (
  varieties,
  soldOutMode,
  isVarietySoldOut = isPosVarietySoldOut
) => {
  const mode = normalizePosSoldOutMode(soldOutMode);
  const list = Array.isArray(varieties) ? varieties : [];
  if (mode !== POS_SOLD_OUT_MODE_HIDDEN) return list;
  return list.filter((variety) => !isVarietySoldOut(variety));
};

export const orderBySoldOutDisplay = (
  items,
  soldOutMode,
  isSoldOut,
  fallbackCompare
) => {
  const mode = normalizePosSoldOutMode(soldOutMode);
  const list = Array.isArray(items) ? items : [];
  const compareFallback = typeof fallbackCompare === 'function'
    ? fallbackCompare
    : () => 0;

  return list
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      if (mode === POS_SOLD_OUT_MODE_BOTTOM && typeof isSoldOut === 'function') {
        const leftSoldOut = Boolean(isSoldOut(left.item));
        const rightSoldOut = Boolean(isSoldOut(right.item));
        if (leftSoldOut !== rightSoldOut) return leftSoldOut ? 1 : -1;
      }
      const fallbackResult = compareFallback(left.item, right.item);
      return fallbackResult || left.index - right.index;
    })
    .map(({ item }) => item);
};

export const prepareProductsForSoldOutDisplay = (
  products,
  soldOutMode,
  isProductSoldOut = isPosProductSoldOut,
  fallbackCompare
) => orderBySoldOutDisplay(
  filterProductsBySoldOutDisplay(products, soldOutMode, isProductSoldOut),
  soldOutMode,
  isProductSoldOut,
  fallbackCompare
);

export const prepareVarietiesForSoldOutDisplay = (
  varieties,
  soldOutMode,
  isVarietySoldOut = isPosVarietySoldOut,
  fallbackCompare
) => orderBySoldOutDisplay(
  filterVarietiesBySoldOutDisplay(varieties, soldOutMode, isVarietySoldOut),
  soldOutMode,
  isVarietySoldOut,
  fallbackCompare
);
